import React, { useState, useEffect, useContext, Suspense } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import Gettext from "../common/Gettext";
import { Link as _Link } from "../common/Link";
import { fontSize } from "../common/styled-mixins";
import CloseIcon from "../Icons/Close";
import TournamentApi from "../Tournaments/Api";

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 20003;
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 740px;
  padding: 40px 35px 32px;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#CCCCCC")};
  background-color: ${({ theme }) =>
    theme.mode === "light" ? "#F5F5F5" : "#272B34"};
  border-radius: 4px;
  text-align: center;

  @media (max-width: 959px) {
    top: 0;
    left: 0;
    transform: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 32px 16px 38px;
    border-radius: 0;
  }

  @media (max-height: 500px) {
    overflow-y: auto;
    position: relative;
    justify-content: space-between;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 19px;
  right: 19px;
  display: block;
  width: 20px;
  height: 20px;
  padding: 0;
  color: #717171;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const Title = styled.div`
  margin-bottom: 33px;
  font-size: ${fontSize(28)};
  line-height: 1.2;
  color: ${({ theme }) => (theme.mode === "light" ? "#343434" : "#ffffff")};
  font-weight: 700;

  @media (max-width: 959px) {
    margin-bottom: 21px;
  }

  @media (max-height: 500px) {
    margin-bottom: 0;
  }
`;

const Text = styled.div`
  font-size: ${fontSize(14)};
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === "light" ? "#919191" : "#CCCCCC")};
  margin-bottom: 20px;

  @media (max-width: 959px) {
    font-size: ${fontSize(14)};
  }

  @media (max-height: 500px) {
    margin-bottom: 10px;
  }
`;

const Link = styled(_Link)`
  color: ${({ theme }) => (theme.mode === "light" ? "#0085EB" : "#FFCC00")};
  &:hover {
    text-decoration: none;
  }
`;

const Img = styled.img`
  flex-shrink: 0;
  width: 157px;
  height: 136px;
  margin: 0 auto 23px;

  @media (max-height: 500px) {
    margin-bottom: 0;
  }
`;

const Percent = styled.div`
  font-size: ${fontSize(36)};
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === "light" ? "#0085EB" : "#FFCC00")};
  margin-bottom: 19px;
  font-weight: 500;

  @media (max-width: 959px) {
    margin-bottom: 21px;
    font-weight: bold;
  }

  @media (max-height: 500px) {
    margin-bottom: 0;
  }
`;

const ButtonBlock = styled.div`
  display: flex;
  justify-content: center;
`;

const Button = styled.button`
  position: relative;
  min-width: 140px;
  padding: 12px 36px;
  font-weight: bold;
  font-size: ${fontSize(12)};
  color: #ffffff;
  text-decoration: none;
  text-transform: uppercase;
  background-image: linear-gradient(to right, #47c0c8 0%, #5c6cc0 100%);
  border: none;
  border-radius: 32px;
  cursor: pointer;
  z-index: 2;

  ::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: block;
    background-image: linear-gradient(to right, #5c6cc0 0%, #47c0c8 100%);
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.5s;
  }

  :hover::before {
    opacity: 1;
  }
`;

const EarlySalePopup = ({ activeStock, dispatch }) => {
  // Попап актива дня. Вылезает при старте при условии, что:
  // - Актив дня есть
  // - Клиент им уже не торгует
  // - В этой сессии пока не вылезал

  // Используем новомодные апи, чтобы не иметь дела с недостатком данных
  let api = useContext(TournamentApi);
  let assetsEntity = api.readEntity("/api/getAssets");
  if (assetsEntity.status === "Pending" && assetsEntity.value == null) {
    // Мне стыдно, что такая деталь просочилась в продуктовый код, работаем с
    // тем, что есть. (throw Promise даёт реакту понять, что мы пока не готовы)
    throw assetsEntity.promise;
  }

  let assets = assetsEntity.value;

  let assetOfTheDay = null;

  // Ходим в поисках актива дня. Помним, что его может и не быть.

  if (assets) {
    cycle: for (let category of Object.keys(assets)) {
      for (let asset of assets[category]) {
        if (asset.pickOfTheDay === true) {
          assetOfTheDay = asset;
          break cycle;
        }
      }
    }
  }

  // Preseerved Stock of The Day — это айди актива, попап о котором мы уже пока-
  // зывали. Нужен чтобы не повторяться.

  let [preservedStockOfTheDay] = useState(() => {
    try {
      return sessionStorage.getItem("stock_of_the_day");
    } catch (error) {
      return null;
    }
  });

  // Переменная engaged диктует, показывать попап или нет. Мы включаем её по-
  // умолчанию только если это уместно:

  const [engaged, setEngaged] = useState(
    assetOfTheDay != null &&
      assetOfTheDay.stock !== activeStock &&
      assetOfTheDay.stock !== preservedStockOfTheDay
  );

  // Эффект для запоминания уже показанного в рамках сесии.

  useEffect(() => {
    if (engaged && assetOfTheDay != null) {
      try {
        sessionStorage.setItem("stock_of_the_day", assetOfTheDay.stock);
      } catch (error) {}
    }
  }, [engaged, assetOfTheDay]);

  function onClose() {
    setEngaged(false);
  }

  function onAccept() {
    setEngaged(false);
    dispatch({
      type: "SWITCH_TO_STOCK",
      payload: assetOfTheDay.stock
    });
  }

  if (!engaged || assetOfTheDay == null) {
    return null;
  }

  let { stock, profit } = assetOfTheDay;

  return (
    <Wrapper>
      <Overlay onClick={onClose} />
      <Container>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        <Title>
          <Gettext>Get higher profit!</Gettext>
        </Title>
        <Img
          src={`/img/flags-analytics/${stock.toLowerCase()}.png`}
          alt={stock.toLowerCase()}
          onError={e => {
            e.target.onerror = null;
            e.target.style = "display:none";
          }}
        />

        {profit && <Percent>{profit}%</Percent>}
        <Text>
          <Gettext>
            Today {stock} is traded with a higher profit percentage!
          </Gettext>
        </Text>
        <Text>
          {/*prettier-ignore*/}
          <Gettext>
            Start trading more efficiently with this <Link to="/news/profitpercent"><Gettext>tutorial about profit percentage</Gettext></Link>.
          </Gettext>
        </Text>
        <ButtonBlock>
          <Button onClick={onAccept}>
            <Gettext>Try now</Gettext>
          </Button>
        </ButtonBlock>
      </Container>
    </Wrapper>
  );
};

function Popup({ activeStock, dispatch }) {
  // Магия Suspense — ради чего выше был throw Promise. Будет показывать
  // fallback пока данные не готовы! В данном случае не покажет ничего!
  return (
    <Suspense fallback={null}>
      <EarlySalePopup activeStock={activeStock} dispatch={dispatch} />
    </Suspense>
  );
}

function mapStateToProps(state) {
  return {
    activeStock: state.tabs.tabs[state.tabs.activeTab]
  };
}

export default connect(mapStateToProps)(Popup);



// WEBPACK FOOTER //
// ./src/components/TradeModals/ProfitableTerms.js